<template>
	<div class="script-editor">
		<div
			ref="editorContainer"
			class="script-editor__container"
			:class="{
				'script-editor__container--disabled': disabled
			}"
		/>
	</div>
</template>

<script>
import CodeMirror from 'codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/mode/javascript/javascript'
import 'codemirror/addon/edit/matchbrackets'
import 'codemirror/addon/edit/closebrackets'
import 'codemirror/addon/selection/active-line'
import 'codemirror/addon/fold/foldcode'
import 'codemirror/addon/fold/foldgutter'
import 'codemirror/addon/fold/foldgutter.css'
import 'codemirror/addon/fold/brace-fold'
import 'codemirror/addon/display/placeholder'
import 'codemirror/addon/hint/show-hint'
import 'codemirror/addon/hint/show-hint.css'
import 'codemirror/addon/hint/javascript-hint'
import 'codemirror/addon/lint/lint'
import 'codemirror/addon/lint/lint.css'
import 'codemirror/theme/material-darker.css'

import { JintValidator } from './validation/JintValidator.js'
import { MarkerSeverity } from './validation/jint-patterns.js'

/**
 * Autocomplete definitions for injected runtime objects.
 * These mirror the TypeScript declarations used for IntelliSense.
 */
const SYSTEM_COMPLETIONS = [
	{ text: 'current_datetime', displayText: 'current_datetime — Current date/time in ISO format' },
	{ text: 'current_date', displayText: 'current_date — Current date (YYYY-MM-DD)' },
	{ text: 'current_time', displayText: 'current_time — Current time (HH:mm:ss)' },
	{ text: 'timestamp', displayText: 'timestamp — Unix timestamp (seconds)' },
	{ text: 'timestamp_ms', displayText: 'timestamp_ms — Unix timestamp (milliseconds)' },
	{ text: 'current_year', displayText: 'current_year — Current year' },
	{ text: 'current_month', displayText: 'current_month — Current month (1-12)' },
	{ text: 'current_day', displayText: 'current_day — Current day (1-31)' },
	{ text: 'current_hour', displayText: 'current_hour — Current hour (0-23)' },
	{ text: 'current_minute', displayText: 'current_minute — Current minute (0-59)' },
	{ text: 'current_weekday', displayText: 'current_weekday — Day name (e.g. "Monday")' },
	{ text: 'week_number', displayText: 'week_number — ISO week number (1-53)' },
	{ text: 'quarter', displayText: 'quarter — Quarter (Q1-Q4)' },
	{ text: 'utc_now', displayText: 'utc_now — UTC now in ISO format' },
	{ text: 'date_compact', displayText: 'date_compact — Compact date (YYYYMMDD)' },
	{ text: 'time_compact', displayText: 'time_compact — Compact time (HHmmss)' },
	{ text: 'guid', displayText: 'guid — Generated GUID' },
	{ text: 'short_guid', displayText: 'short_guid — Short GUID' },
	{ text: 'random_guid', displayText: 'random_guid — Random GUID (new each access)' },
	{ text: 'random_number', displayText: 'random_number — Random number (1-1,000,000)' },
	{ text: 'nonce', displayText: 'nonce — Secure random nonce' },
	{ text: 'request_id', displayText: 'request_id — Request ID' },
	{ text: 'service_hash', displayText: 'service_hash — SHA-256 hash of service ID' },
	{ text: 'integration_hash', displayText: 'integration_hash — SHA-256 hash of integration system ID' },
	{ text: 'request_hash', displayText: 'request_hash — SHA-256 hash of request ID' },
	{ text: 'hash(', displayText: 'hash(value) — SHA-256 hash of any string' },
	{ text: 'base64_timestamp', displayText: 'base64_timestamp — Base64-encoded timestamp' },
	{ text: 'url_safe_timestamp', displayText: 'url_safe_timestamp — URL-safe timestamp' }
]

const ENCODE_COMPLETIONS = [
	{ text: 'base64(', displayText: 'base64(value) — Base64 encode' },
	{ text: 'url(', displayText: 'url(value) — URL encode' },
	{ text: 'html(', displayText: 'html(value) — HTML encode' },
	{ text: 'form(', displayText: 'form(value) — Form encode (spaces to +)' },
	{ text: 'hex(', displayText: 'hex(value) — Hexadecimal encode' }
]

const DECODE_COMPLETIONS = [
	{ text: 'base64(', displayText: 'base64(value) — Base64 decode' },
	{ text: 'url(', displayText: 'url(value) — URL decode' },
	{ text: 'html(', displayText: 'html(value) — HTML decode' },
	{ text: 'form(', displayText: 'form(value) — Form decode (+ to spaces)' },
	{ text: 'hex(', displayText: 'hex(value) — Hexadecimal decode' }
]

const CONFIG_COMPLETIONS = [
	{ text: 'service_id', displayText: 'service_id — Numeric service ID' },
	{ text: 'integration_id', displayText: 'integration_id — Integration ID' },
	{ text: 'integration_system_id', displayText: 'integration_system_id — Integration system ID' },
	{ text: 'api_type', displayText: 'api_type — API type (e.g. "rest", "soap")' },
	{ text: 'is_json', displayText: 'is_json — True if request body contains JSON' }
]

const TOP_LEVEL_COMPLETIONS = [
	{ text: 'data', displayText: 'data — Source data fields' },
	{ text: 'system', displayText: 'system — Date/time, identity, and security functions' },
	{ text: 'encode', displayText: 'encode — Encoding functions (base64, url, html, form, hex)' },
	{ text: 'decode', displayText: 'decode — Decoding functions (base64, url, html, form, hex)' },
	{ text: 'config', displayText: 'config — Integration configuration settings' }
]

/**
 * Map theme prop values to CodeMirror theme names.
 * CodeMirror's default (empty string) is a clean light theme.
 */
const THEME_MAP = {
	vs: 'default',
	'vs-dark': 'material-darker'
}

/**
 * Unscoped CodeMirror height overrides injected via Constructable Stylesheet
 * (CSP-compliant — no inline <style> element). Shared across all instances.
 */
var editorStyleSheet = null
function ensureEditorStyles () {
	if (editorStyleSheet) return
	editorStyleSheet = new CSSStyleSheet()
	editorStyleSheet.replaceSync(
		'.script-editor .CodeMirror-scroll { min-height: 400px !important; padding-bottom: 20px !important; }' +
		'.script-editor .CodeMirror { height: 400px !important; min-height: 400px !important; }' +
		'.script-editor .CodeMirror-gutters { min-height: 400px !important; }'
	)
	document.adoptedStyleSheets = [].concat(document.adoptedStyleSheets || [], [editorStyleSheet])
}

export default {
	name: 'script-editor',
	props: {
		value: {
			type: String,
			required: true
		},
		disabled: {
			type: Boolean,
			default: false
		},
		placeholder: {
			type: String,
			default: '// Enter your JavaScript mapping code here...\n// Return the object to be sent in the request payload\n\nreturn {\n  // your mapping logic\n}'
		},
		language: {
			type: String,
			default: 'javascript'
		},
		theme: {
			type: String,
			default: 'vs'
		},
		availableFields: {
			type: Array,
			default: () => []
		},
		/**
		 * Enable Jint-specific validation.
		 * When true, validates code against Jint runtime constraints.
		 */
		enableJintValidation: {
			type: Boolean,
			default: true
		}
	},
	data () {
		return {
			isInitialized: false
		}
	},
	computed: {
		resolvedTheme () {
			return THEME_MAP[this.theme] || 'default'
		},

		/**
		 * Build autocomplete items from the availableFields prop.
		 * Field names are lowercased to match runtime data object access pattern.
		 */
		dataFieldCompletions () {
			if (!this.availableFields || !this.availableFields.length) return []

			return this.availableFields.map(field => {
				const originalName = field.fieldName || field.name
				const fieldName = originalName ? originalName.toLowerCase() : originalName
				const displayName = field.displayName || originalName
				const typeName = field.typeFriendlyName || field.dataType || 'any'
				return {
					text: fieldName,
					displayText: fieldName + ' — ' + displayName + ' (' + typeName + ')'
				}
			})
		}
	},
	watch: {
		value (newValue) {
			if (this.editor && this.editor.getValue() !== newValue) {
				// Preserve cursor position across external value updates
				var cursor = this.editor.getCursor()
				this.editor.setValue(newValue || '')
				this.editor.setCursor(cursor)
			}
		},
		disabled (newValue) {
			if (this.editor) {
				this.editor.setOption('readOnly', newValue)
			}
		},
		availableFields: {
			handler () {
				// Completions update reactively via the computed property;
				// no additional action required.
			},
			deep: true
		}
	},
	created () {
		// Non-reactive properties — CodeMirror's editor instance should not be
		// observed by Vue 2's reactivity system.
		this.editor = null
		this.jintValidator = null
	},
	mounted () {
		this.$nextTick(() => {
			this.initializeEditor()
		})
	},
	beforeDestroy () {
		if (this.editor) {
			try {
				// Remove CodeMirror's DOM from the container — the editor was created
				// via CodeMirror(element), not fromTextArea(), so toTextArea() doesn't exist.
				var wrapper = this.editor.getWrapperElement()
				if (wrapper && wrapper.parentNode) {
					wrapper.parentNode.removeChild(wrapper)
				}
			} catch (e) {
				// Swallow DOM cleanup errors during teardown
			}
			this.editor = null
		}
	},
	methods: {
		initializeEditor () {
			if (this.isInitialized || !this.$refs.editorContainer) return

			try {
				// Initialise Jint validator if enabled
				if (this.enableJintValidation) {
					this.jintValidator = new JintValidator()
				}

				var self = this

				var gutters = ['CodeMirror-linenumbers', 'CodeMirror-foldgutter']
				if (this.enableJintValidation) {
					gutters.push('CodeMirror-lint-markers')
				}

				this.editor = CodeMirror(this.$refs.editorContainer, {
					value: this.value || this.placeholder,
					mode: this.language,
					theme: this.resolvedTheme,
					readOnly: this.disabled,
					lineNumbers: true,
					matchBrackets: true,
					autoCloseBrackets: true,
					styleActiveLine: true,
					foldGutter: true,
					gutters: gutters,
					lineWrapping: true,
					tabSize: 2,
					indentWithTabs: false,
					placeholder: this.placeholder,
					lint: this.enableJintValidation
						? { getAnnotations: function (text) { return self.lintCode(text) }, delay: 300 }
						: false,
					extraKeys: {
						'Ctrl-Space': 'autocomplete'
					},
					hintOptions: {
						hint: function (editor) { return self.getHints(editor) },
						completeSingle: false
					}
				})

				// Emit changes to parent (v-model support)
				this.editor.on('change', function () {
					var newValue = self.editor.getValue()
					if (newValue !== self.value) {
						self.$emit('input', newValue)
					}
				})

				// Auto-trigger autocomplete when a dot is typed after a known object
				this.editor.on('inputRead', function (editor, change) {
					if (change.text[0] === '.') {
						editor.showHint({ completeSingle: false })
					}
				})

				// Editor sizing has to be injected at document level: this file's
				// scoped `:deep()` rules are Vue 3 syntax that vite-plugin-vue2
				// leaves untransformed, so they compile to an invalid selector and
				// the browser discards them.
				ensureEditorStyles()

				this.isInitialized = true

				// Run initial validation
				if (this.enableJintValidation && this.jintValidator) {
					var initialResult = this.jintValidator.validate(this.value || '')
					this.emitValidationResult(initialResult)
				}
			} catch (error) {
				console.error('Failed to initialise Script Editor:', error)
			}
		},

		/**
		 * Custom hint function providing autocomplete for data, system, encode, decode, and config objects.
		 */
		getHints (editor) {
			var cursor = editor.getCursor()
			var line = editor.getLine(cursor.line)
			var end = cursor.ch

			// Walk backwards to find the token (word characters and dots)
			var start = end
			while (start > 0 && /[\w.]/.test(line.charAt(start - 1))) {
				start--
			}

			var token = line.slice(start, end)

			// After a dot — provide property/method completions
			var dotIndex = token.lastIndexOf('.')
			if (dotIndex >= 0) {
				var objName = token.slice(0, dotIndex)
				var partial = token.slice(dotIndex + 1).toLowerCase()

				var completions = []
				if (objName === 'data') {
					completions = this.dataFieldCompletions
				} else if (objName === 'system') {
					completions = SYSTEM_COMPLETIONS
				} else if (objName === 'encode') {
					completions = ENCODE_COMPLETIONS
				} else if (objName === 'decode') {
					completions = DECODE_COMPLETIONS
				} else if (objName === 'config') {
					completions = CONFIG_COMPLETIONS
				}

				if (completions.length) {
					var filtered = partial
						? completions.filter(function (c) { return c.text.toLowerCase().indexOf(partial) === 0 })
						: completions

					if (filtered.length) {
						return {
							list: filtered,
							from: CodeMirror.Pos(cursor.line, start + dotIndex + 1),
							to: CodeMirror.Pos(cursor.line, end)
						}
					}
				}
			}

			// Top-level completions (data, system, encode, decode, config)
			if (token.length >= 1 && dotIndex < 0) {
				var lowerToken = token.toLowerCase()
				var topLevel = TOP_LEVEL_COMPLETIONS.filter(function (c) {
					return c.text.toLowerCase().indexOf(lowerToken) === 0
				})

				if (topLevel.length) {
					return {
						list: topLevel,
						from: CodeMirror.Pos(cursor.line, start),
						to: CodeMirror.Pos(cursor.line, end)
					}
				}
			}

			// Fall back to built-in JavaScript IntelliSense (keywords, identifiers in scope)
			return CodeMirror.hint.javascript(editor)
		},

		/**
		 * Lint adapter — runs JintValidator and converts markers to CodeMirror annotations.
		 */
		lintCode (text) {
			if (!this.jintValidator) return []

			// Skip validation for empty code
			if (!text || !text.trim()) {
				this.emitValidationResult({
					hasErrors: false,
					hasWarnings: false,
					blockedPatterns: [],
					es6Features: [],
					suspiciousPatterns: []
				})
				return []
			}

			var result = this.jintValidator.validate(text)

			// Notify parent of validation state
			this.emitValidationResult(result)

			// Convert validation markers to CodeMirror lint annotations
			return result.markers.map(function (marker) {
				var severity
				if (marker.severity === MarkerSeverity.Error) {
					severity = 'error'
				} else if (marker.severity === MarkerSeverity.Warning) {
					severity = 'warning'
				} else {
					severity = 'info'
				}

				return {
					from: CodeMirror.Pos(marker.startLineNumber - 1, marker.startColumn - 1),
					to: CodeMirror.Pos(marker.endLineNumber - 1, marker.endColumn - 1),
					message: marker.message,
					severity: severity
				}
			})
		},

		/**
		 * Emit validation-change event to parent component.
		 */
		emitValidationResult (result) {
			this.$emit('validation-change', {
				hasErrors: result.hasErrors,
				hasWarnings: result.hasWarnings,
				blockedPatterns: result.blockedPatterns || [],
				es6Features: result.es6Features || [],
				suspiciousPatterns: result.suspiciousPatterns || []
			})
		}
	}
}
</script>

<style lang="scss" scoped>
.script-editor {
	--editor-font: 'Consolas', 'Monaco', 'Lucida Console', 'Liberation Mono', 'DejaVu Sans Mono', 'Bitstream Vera Sans Mono', 'Courier New', monospace;
	font-family: unset !important;
	position: relative;
	height: 100%;
	display: flex;
	flex-direction: column;

	&__container {
		flex: 1;
		position: relative;
		border: 1px solid #edebeb;
		border-radius: var(--radius-sm);
		background: #f5f5f5; // Match gutter colour so scrolling looks seamless
		overflow: hidden; // CodeMirror handles its own scrolling

		&--disabled {
			opacity: 0.6;
		}
	}
}

// CodeMirror font styling - Maximum specificity to override app.vue universal selector
.script-editor :deep(.CodeMirror),
.script-editor :deep(.CodeMirror) *,
.script-editor :deep(.CodeMirror) *::before,
.script-editor :deep(.CodeMirror) *::after {
	font-family: var(--editor-font) !important;
	font-size: 14px !important;
	line-height: 1.4 !important;
}

.script-editor :deep(.CodeMirror-line),
.script-editor :deep(.CodeMirror-line) *,
.script-editor :deep(.CodeMirror-line) *::before,
.script-editor :deep(.CodeMirror-line) *::after {
	font-family: var(--editor-font) !important;
	font-size: 14px !important;
	line-height: 1.4 !important;
}

.script-editor :deep(.CodeMirror pre),
.script-editor :deep(.CodeMirror pre) *,
.script-editor :deep(.CodeMirror pre) *::before,
.script-editor :deep(.CodeMirror pre) *::after {
	font-family: var(--editor-font) !important;
	font-size: 14px !important;
	line-height: 1.4 !important;
}

// === CodeMirror global theme overrides ===
// Editor sizing deliberately lives in ensureEditorStyles() and nowhere else.
// It must not be duplicated here: a definite height on .CodeMirror is what lets
// CodeMirror enable its own vertical scrollbar, and a competing `height: 100%`
// in this block silently disabled it (AB#26252).
.script-editor :deep(.CodeMirror) {
	font-family: var(--editor-font) !important;
	font-size: 13px;
	line-height: 1.5;
	border: none;
	background: #fafafa;
	color: #37474f;
	font-weight: 400;
}

.script-editor :deep(.CodeMirror.CodeMirror-readonly) {
	background: #f5f5f5;
	color: #9e9e9e;
}

.script-editor :deep(.CodeMirror-selected) {
	background: #e3f2fd;
}

.script-editor :deep(.CodeMirror-line) {
	padding: 0 4px;
}

.script-editor :deep(.CodeMirror-gutters) {
	border-right: 1px solid #e0e0e0;
	background: #f5f5f5;
}

.script-editor :deep(.CodeMirror-linenumber) {
	color: #9e9e9e;
	padding: 0 8px;
	font-size: 12px;
}

.script-editor :deep(.CodeMirror-foldgutter) {
	width: 16px;
}

.script-editor :deep(.CodeMirror-foldgutter-open),
.script-editor :deep(.CodeMirror-foldgutter-folded) {
	color: #666;
	cursor: pointer;
}

.script-editor :deep(.CodeMirror-activeline-background) {
	background: #f0f4f8;
}

.script-editor :deep(.CodeMirror-matchingbracket) {
	color: #007bff !important;
	font-weight: bold !important;
}

.script-editor :deep(.CodeMirror-nonmatchingbracket) {
	background: rgba(220, 38, 38, 0.08);
	color: #dc2626;
}

.script-editor :deep(.CodeMirror-cursor) {
	border-left: 2px solid #333;
}

.script-editor :deep(.CodeMirror-placeholder) {
	color: #999;
	font-style: italic;
}

// Lint marker styling
.script-editor :deep(.CodeMirror-lint-marker.CodeMirror-lint-marker-error) {
	background: #dc2626;
	border-radius: 50%;
	color: #fff;
	cursor: pointer;
	font-size: 12px;
	font-weight: bold;
	height: 16px;
	line-height: 16px;
	margin-left: 2px;
	text-align: center;
	width: 16px;
}

.script-editor :deep(.CodeMirror-lint-marker.CodeMirror-lint-marker-warning) {
	background: #ea580c;
	border-radius: 50%;
	color: #fff;
	cursor: pointer;
	font-size: 12px;
	font-weight: bold;
	height: 16px;
	line-height: 16px;
	margin-left: 2px;
	text-align: center;
	width: 16px;
}

.script-editor :deep(.CodeMirror-lint-tooltip) {
	background: #0B3142;
	border: 1px solid #406A94;
	border-radius: var(--radius-sm);
	color: #fff;
	font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
	font-size: 12px;
	max-width: 300px;
	padding: 8px 12px;
	position: absolute;
	z-index: 1000;
}

// Syntax highlighting
.script-editor :deep(.cm-string) {
	color: #2e7d32 !important;
}

.script-editor :deep(.cm-property) {
	color: #1565c0 !important;
	font-weight: 400;
}

.script-editor :deep(.cm-number) {
	color: #d84315 !important;
}

.script-editor :deep(.cm-atom) {
	color: #7b1fa2 !important;
}

.script-editor :deep(.cm-keyword) {
	color: #7b1fa2 !important;
}

.script-editor :deep(.cm-comment) {
	color: #9e9e9e !important;
	font-style: italic;
}

.script-editor :deep(.CodeMirror:focus-within) {
	box-shadow: 0 0 0 1px #2196f3;
	border-color: #2196f3;
}

// Hint/autocomplete dropdown
.script-editor :deep(.CodeMirror-hints) {
	font-family: var(--editor-font);
	font-size: 13px;
	background: #fff;
	border: 1px solid #ddd;
	border-radius: 4px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	max-height: 200px;
	overflow-y: auto;
	z-index: 1000;
}

.script-editor :deep(.CodeMirror-hint) {
	padding: 4px 8px;
	white-space: nowrap;
	color: #333;
}

.script-editor :deep(.CodeMirror-hint-active) {
	background: #1976d2;
	color: #fff;
}

// Dark theme overrides (material-darker)
.script-editor :deep(.CodeMirror.cm-s-material-darker) {
	background: #212121;
}

.script-editor :deep(.cm-s-material-darker .CodeMirror-gutters) {
	background: #1a1a1a;
	border-right: 1px solid #333;
}

.script-editor :deep(.cm-s-material-darker .CodeMirror-linenumber) {
	color: #666;
}

.script-editor :deep(.cm-s-material-darker .CodeMirror-cursor) {
	border-left-color: #fff;
}

.script-editor :deep(.cm-s-material-darker .CodeMirror-selected) {
	background: #3a3a3a;
}

.script-editor :deep(.cm-s-material-darker .CodeMirror-activeline-background) {
	background: rgba(255, 255, 255, 0.05);
}
</style>
